/**
 * ChatContainer Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.1 - Chat UI Component
 *
 * Main chat component that manages state and orchestrates child components.
 */

import React, { useState, useCallback, useEffect } from 'react';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import StarterPrompts from './StarterPrompts';
import { createLogger } from '../../utils/logger';

// Create logger for chat context
const logger = createLogger('CHAT');

// localStorage keys for session persistence
const STORAGE_KEYS = {
  MESSAGES: 'raja_chat_messages',
  TIMESTAMP: 'raja_chat_timestamp',
  SESSION_ID: 'raja_chat_session_id',
};
const SESSION_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

const ChatContainer = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [showIdeasDropdown, setShowIdeasDropdown] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // T2d.2: Load messages from localStorage on mount
  useEffect(() => {
    try {
      const savedMessages = localStorage.getItem(STORAGE_KEYS.MESSAGES);
      const savedTimestamp = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
      const savedSessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);

      if (savedMessages && savedTimestamp) {
        const age = Date.now() - parseInt(savedTimestamp, 10);

        if (age < SESSION_MAX_AGE) {
          const parsed = JSON.parse(savedMessages);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setMessages(parsed);
            if (savedSessionId) {
              setSessionId(savedSessionId);
            }
            logger.info('Restored chat session from localStorage', {
              messageCount: parsed.length,
              ageMinutes: Math.round(age / 60000),
            });
          }
        } else {
          // Clear stale data
          localStorage.removeItem(STORAGE_KEYS.MESSAGES);
          localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
          localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
          logger.debug('Cleared stale chat session', { ageHours: Math.round(age / 3600000) });
        }
      }
    } catch (err) {
      logger.warn('Failed to restore chat session', { error: err.message });
    }
    setIsInitialized(true);
  }, []);

  // T2d.1: Save messages to localStorage when they change
  useEffect(() => {
    if (!isInitialized) return; // Don't save during initial load

    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages));
        localStorage.setItem(STORAGE_KEYS.TIMESTAMP, Date.now().toString());
        if (sessionId) {
          localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
        }
      } catch (err) {
        logger.warn('Failed to save chat session', { error: err.message });
      }
    }
  }, [messages, sessionId, isInitialized]);

  // T2d.3: Clear chat function
  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
    setShowIdeasDropdown(false);
    localStorage.removeItem(STORAGE_KEYS.MESSAGES);
    localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    logger.info('Chat session cleared by user');
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;

    const startTime = performance.now();

    // Clear any previous error
    setError(null);

    // Story: User initiated a conversation
    logger.info('User sending message', {
      messageLength: text.length,
      sessionId: sessionId || 'new',
      messageCount: messages.length + 1,
    });

    // Add user message to the list
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Set loading state
    setIsLoading(true);
    logger.debug('Calling chat API', { endpoint: '/api/chat' });

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          sessionId: sessionId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Story: API returned an error
        logger.warn('API returned error response', {
          status: response.status,
          errorCode: data.detail?.code,
          error: data.detail?.error,
        });
        throw new Error(data.detail?.error || 'Failed to get response');
      }

      // Update session ID if returned
      if (data.sessionId && !sessionId) {
        logger.debug('New session created', { sessionId: data.sessionId });
        setSessionId(data.sessionId);
      }

      // Add assistant message
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Story: Conversation turn completed successfully
      const duration = Math.round(performance.now() - startTime);
      logger.info('Message exchange completed', {
        durationMs: duration,
        responseLength: data.reply.length,
        sessionId: data.sessionId,
        totalMessages: messages.length + 2,
      });

    } catch (err) {
      // Story: Something went wrong
      const duration = Math.round(performance.now() - startTime);
      logger.error('Chat request failed', {
        error: err.message,
        durationMs: duration,
        sessionId: sessionId,
      });
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, messages.length]);

  const clearError = useCallback(() => {
    logger.debug('Error dismissed by user');
    setError(null);
  }, []);

  // Handle prompt selection - send message and close dropdown
  const handlePromptSelect = useCallback((text) => {
    setShowIdeasDropdown(false);
    sendMessage(text);
  }, [sendMessage]);

  return (
    <div className="flex flex-col h-[85vh] sm:h-[700px] md:h-[800px] max-w-5xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-slate-600 overflow-hidden transition-colors">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-600 to-sky-700 dark:from-sky-700 dark:to-sky-800 text-white px-4 py-3 flex justify-between items-start">
        <div>
          <h2 className="text-lg font-semibold">Chat with Raja's AI Assistant</h2>
          <p className="text-sm text-sky-100 dark:text-sky-200">Ask me about Raja's experience, skills, or projects</p>
        </div>
        {/* T2d.3: Clear Chat Button */}
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-2.5 py-2 text-sky-200 hover:text-white hover:bg-sky-500/30 rounded-lg transition-colors disabled:opacity-50 shrink-0"
            aria-label="Start a new session"
            title="Start a new session"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span className="text-sm font-medium whitespace-nowrap">New session</span>
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800 px-4 py-2 flex justify-between items-center">
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {/* Starter prompts (shown only when chat is empty) */}
      {messages.length === 0 && !isLoading && (
        <StarterPrompts onSelect={handlePromptSelect} />
      )}

      {/* Messages area (shown when there are messages OR loading) */}
      {(messages.length > 0 || isLoading) && (
        <MessageList messages={messages} isLoading={isLoading} />
      )}

      {/* Input */}
      <ChatInput onSend={sendMessage} disabled={isLoading} />

      {/* Need Ideas button and dropdown (always shown when messages exist) */}
      {messages.length > 0 && (
        <div className="relative border-t border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800">
          <div className="px-4 py-2">
            <button
              onClick={() => setShowIdeasDropdown(!showIdeasDropdown)}
              disabled={isLoading}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="font-semibold">Need Ideas?</span>
              <svg className={`w-3 h-3 transition-transform ${showIdeasDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>

          {/* Dropdown content - absolute positioned to avoid layout shift */}
          {showIdeasDropdown && (
            <div className="absolute bottom-full left-0 right-0 px-4 py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-t-lg shadow-lg">
              <StarterPrompts onSelect={handlePromptSelect} compact />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
